import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { CgSpinner } from 'react-icons/cg'
import { IoExtensionPuzzleOutline } from 'react-icons/io5'

import { OpsGroupsListItem } from '@/models/operations/types'
import { ButtonApp, Container, Spinner } from '@/components'
import { resolveRequestError } from '@/services/operations'
import { useNotify } from '@/hooks'
import { classNames } from '@/utils'

import { BulkActionConfirmModal } from './components/BulkActionConfirmModal'
import { OpsGroupCard } from './components/OpsGroupCard'
import { OpsGroupCardSkeleton } from './components/OpsSkeletons'
import { OPS_BULK_REFRESH_LABEL, OPS_BULK_REFRESH_LOADING_LABEL, OPS_BUTTON_ICON_CLASS, OPS_BUTTON_INNER_CLASS } from './components/opsUiShared'
import { useOperationsGroups } from './OperationsGroupsProvider'

export const OpsGroupsDashboard = () => {
  const navigate = useNavigate()
  const { notifyError } = useNotify()

  const {
    groups,
    isInitialMetadataLoading,
    isListInProgress,
    listStoreInfo,
    startBulkAction,
    startRefresh,
    refreshGroup,
    isGroupRefreshing,
    isDeployActionsLockedForGroup,
  } = useOperationsGroups()

  const [confirmState, setConfirmState] = useState<{
    group: OpsGroupsListItem
    type: 'redeploy' | 'clear-cache'
  } | null>(null)
  const [isBulkPrefetching, setIsBulkPrefetching] = useState(false)

  const handleNavigate = useCallback(
    (groupId: string) => {
      const target = groups.find((g) => g.id === groupId)
      if (target?.loadError) return
      navigate(`/operations/groups/${groupId}`)
    },
    [navigate, groups],
  )

  const handleConfirmBulk = async () => {
    if (!confirmState) return

    const groupId = confirmState.group.id

    setIsBulkPrefetching(true)
    try {
      const stores = await listStoreInfo(groupId)
      const activeStores = stores.filter((s) => s.isActive)

      if (!activeStores.length) {
        notifyError('No active stores loaded for this group. Try checking status first.')
        setConfirmState(null)
        return
      }

      startBulkAction(confirmState.type, stores, groupId)
      setConfirmState(null)
    } catch (error) {
      notifyError(resolveRequestError(error, 'Failed to load stores for this group'))
      setConfirmState(null)
    } finally {
      setIsBulkPrefetching(false)
    }
  }

  return (
    <>
      {isInitialMetadataLoading && <Spinner />}
      <Container
        className="grow min-h-0"
        contentClassName="flex flex-col min-h-0 overflow-hidden"
        header={
          <div className="flex xs:flex-col-reverse md:flex-row md:justify-between gap-[15px]">
            <p className="text-2xl font-bold">Groups</p>
            <ButtonApp
              isPrimary
              handleClick={startRefresh}
              isLoading={isListInProgress}
              isDisabled={isListInProgress}
            >
              <span className={OPS_BUTTON_INNER_CLASS}>
                {isListInProgress ? (
                  <CgSpinner className={classNames(OPS_BUTTON_ICON_CLASS, 'animate-spin text-white')} />
                ) : (
                  <ArrowPathIcon className={classNames(OPS_BUTTON_ICON_CLASS, 'text-white')} />
                )}
                <span>{isListInProgress ? OPS_BULK_REFRESH_LOADING_LABEL : OPS_BULK_REFRESH_LABEL}</span>
              </span>
            </ButtonApp>
          </div>
        }
      >
        {groups.length > 0 ? (
          <div className="w-full h-full grow overflow-y-auto grid grid-cols-[repeat(_auto-fill,minmax(450px,1fr)_)] 4xl:grid-cols-[repeat(_auto-fill,minmax(550px,1fr)_)] items-stretch gap-6 auto-rows-max">
            {groups.map((group) =>
              group.isRefreshing ? (
                <OpsGroupCardSkeleton
                  key={group.id}
                  name={group.name}
                  description={group.description}
                />
              ) : (
                <OpsGroupCard
                  key={group.id}
                  group={group}
                  onNavigate={handleNavigate}
                  onRefreshGroup={refreshGroup}
                  onRedeployAll={(g) => setConfirmState({ group: g, type: 'redeploy' })}
                  onClearCacheAll={(g) => setConfirmState({ group: g, type: 'clear-cache' })}
                  isBulkRunning={isDeployActionsLockedForGroup(group.id, group.summary.building)}
                  isGroupRefreshing={isGroupRefreshing(group.id)}
                  isGlobalRefresh={isListInProgress}
                />
              ),
            )}
          </div>
        ) : (
          !isInitialMetadataLoading && (
            <div className="w-full h-full flex flex-col items-center justify-center py-12">
              <IoExtensionPuzzleOutline className="text-[2.5rem] text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900">No groups</h3>
              <p className="mt-1 text-sm text-gray-500">Click Check Status to load deployment status.</p>
            </div>
          )
        )}
      </Container>

      <BulkActionConfirmModal
        visible={!!confirmState}
        actionType={confirmState?.type ?? null}
        groupName={confirmState?.group.name ?? ''}
        storeCount={confirmState?.group.summary.active ?? 0}
        isLoading={isBulkPrefetching}
        onConfirm={handleConfirmBulk}
        onHide={() => setConfirmState(null)}
      />
    </>
  )
}
